import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface BackupRow {
  readonly backupId: string;
  readonly backupReference: string;
  readonly backupType: string;
  readonly status: string;
  readonly isVerified: boolean;
}

export function M19BackupDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<BackupRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [backupReference, setBackupReference] = useState('');
  const [backupType, setBackupType] = useState('FULL');
  const [storageLocation, setStorageLocation] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: BackupRow[] }>('/v1/platform/backups', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/platform/backups', {
        method: 'POST', token,
        body: { backupReference, backupType, storageLocation: storageLocation || undefined },
      });
      setCreated(true);
      setBackupReference('');
      setStorageLocation('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const verify = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/v1/platform/backups/${id}/verify`, {
        method: 'POST', token, body: { verificationChecksum: `chk-${id}` },
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m19.backup.loading')}</p>;
  return (
    <main aria-label="backup-dashboard">
      <h1>{t('m19.backup.title')}</h1>

      <form onSubmit={onCreate} aria-label="backup-create-form">
        <h2>{t('m19.backup.newBackup')}</h2>
        <label htmlFor="bk-ref">{t('m19.backup.labelReference')}</label>
        <input id="bk-ref" value={backupReference} onChange={(e) => setBackupReference(e.target.value)} required />
        <label htmlFor="bk-type">{t('m19.backup.labelType')}</label>
        <select id="bk-type" aria-label="backup-type" value={backupType} onChange={(e) => setBackupType(e.target.value)}>
          <option value="FULL">{t('m19.backup.optFull')}</option>
          <option value="INCREMENTAL">{t('m19.backup.optIncremental')}</option>
          <option value="DIFFERENTIAL">{t('m19.backup.optDifferential')}</option>
          <option value="ON_DEMAND">{t('m19.backup.optOnDemand')}</option>
        </select>
        <label htmlFor="bk-loc">{t('m19.backup.labelStorage')}</label>
        <input id="bk-loc" value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m19.backup.created')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m19.backup.creating') : t('m19.backup.create')}</button>
      </form>

      <table aria-label="backups-table">
        <thead><tr><th>{t('m19.backup.colReference')}</th><th>{t('m19.backup.colType')}</th><th>{t('m19.backup.colStatus')}</th><th>{t('m19.backup.colVerified')}</th><th>{t('m19.backup.colAction')}</th></tr></thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.backupId}>
              <td>{b.backupReference}</td>
              <td>{b.backupType}</td>
              <td>{b.status}</td>
              <td>{b.isVerified ? t('m19.backup.yes') : t('m19.backup.no')}</td>
              <td>
                {!b.isVerified && (
                  <button type="button" aria-label={`verify-${b.backupId}`} disabled={busyId === b.backupId} onClick={() => void verify(b.backupId)}>
                    {busyId === b.backupId ? t('m19.backup.verifying') : t('m19.backup.verify')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
